import { validateApiOrigin, type ApiRoutingPolicy } from '../config/api-routing';
import type { PersonaId } from '../types/persona';
import type { LocalPersonalFilingResponse, PersonalFilingProfile,
  PersonalFilingProfileResponse } from './personal-filing-types';

function sessionHeaders(token:string):Record<string,string> {
  return token.startsWith('cookie:')?{'x-amoye-product':token.slice(7)}:{Authorization:`Bearer ${token}`};
}

export type ServiceState = 'checking' | 'ready' | 'degraded' | 'offline';
export type JobState = 'pending' | 'processing' | 'completed' | 'failed';
export type ReviewState = 'pending' | 'auto_classified' | 'needs_review' | 'approved' | 'rejected';
export interface HomeSuggestionsResponse {
  hints: string[];
  tasks: Array<{ label: string; prompt: string }>;
}

export interface ServiceStatus {
  status: string;
  timestamp: string;
}

export interface AccountSubject {
  id: string;
  display_name: string;
  kind: 'client' | 'business' | 'individual';
  /** Legal nature of the tax subject. `kind` above describes the product
   * relationship and must not be used as a legal tax classification. */
  tax_subject_kind?: 'organization' | 'individual';
  workspace_name?: string;
  tax_id_hint?: string;
  tax_identity_status?: 'NOT_PROVIDED' | 'PENDING' | 'VERIFIED';
  engagement_id?: string;
  context_id?: string;
  context_ids?: Partial<Record<IdentityRail, string>>;
  resolved_contexts?: ResolvedIdentityContext[];
}

export interface AccountSession {
  mfa_required?: boolean;
  mfa_enrollment_required?: boolean;
  passkey_available?: boolean;
  totp_available?: boolean;
  recovery_code_available?: boolean;
  challenge_id?: string;
  challenge_token?: string;
  verification_required?: boolean;
  subscription_required?: boolean;
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  persona: PersonaId;
  product: PersonaId;
  account: { display_name: string; email: string };
  subjects: AccountSubject[];
}

export type CurrentAccountSession = Omit<AccountSession, 'access_token' | 'token_type' | 'expires_in'>;

export interface CreatedTaxSubject {
  id: string;
  display_name: string;
  kind: 'client' | 'business' | 'individual';
  tax_subject_kind: 'organization' | 'individual';
  workspace_name: string;
  tax_id_hint?: string;
  tax_identity_status: 'NOT_PROVIDED' | 'PENDING';
  tax_types: Array<'VAT' | 'PIT' | 'WHT' | 'CIT' | 'PAYE'>;
  setup_state: 'ready';
}
export interface CreatedPersonalTaxYear { tax_year: number; setup_state: 'ready'; }
export interface CommercialPlan {
  id:string;code:string;version:number;product:PersonaId;name:string;description:string;amount_kobo:string;
  currency:'NGN';interval:'MONTHLY'|'ANNUAL';state:'DRAFT'|'PUBLISHED'|'RETIRED';provider_plan_code?:string;
  capabilities:Record<string,number>;created_at:string;updated_at:string;
}
export interface CommercialPurchase { reference:string;product:PersonaId;plan_code:string;amount_kobo:string;
  currency:'NGN';state:'AWAITING_PAYMENT'|'AWAITING_EMAIL_VERIFICATION'|'ACTIVE'|'FAILED'|'EXPIRED';checkout_url:string;expires_at:string;
  account_created?:boolean;verification_required?:boolean;verification_preview_url?:string; }
export interface OperationsSummary {operator_role:'SUPPORT'|'EXECUTIVE';customers:number;active_products:number;attention_events:number;active_grants:number;pending_recovery_requests:number;}
export interface CommercialCustomer {principal_id:string;firm_id:string;display_name:string;email:string;phone?:string;created_at:string;
  products:Array<{product:PersonaId;status:string;subscription_id:string}>;}
export interface CommercialOffer {id:string;code:string;name:string;product:PersonaId;kind:'PAYMENT_BYPASS'|'PERCENT_DISCOUNT'|'FIXED_PRICE'|'TRIAL'|'SERVICE_EXCEPTION';
  value:number;duration_days:number;state:'DRAFT'|'ACTIVE'|'RETIRED';note:string;created_by:string;created_at:string;updated_at:string;}
export interface OperationsEvent {id:string;principal_id?:string;firm_id?:string;category:string;event_type:string;severity:'INFO'|'ATTENTION'|'ERROR';
  summary:string;correlation_id:string;occurred_at:string;metadata:Record<string,unknown>;}
export interface CommercialGrant {id:string;principal_id:string;product:PersonaId;subscription_id:string;offer_id:string;kind:CommercialOffer['kind'];
  state:'ACTIVE'|'REVOKED'|'EXPIRED';prior_amount_kobo:number;effective_amount_kobo:number;starts_at:string;expires_at:string;reason:string;created_by:string;created_at:string;}
type OperatorAccess={key:string;name:string};

export type ProductTrack = 'A' | 'B1' | 'B2';
export type IdentityRail = 'tax_return' | 'e_invoice';

export interface IdentityScope {
  workspaceId: string;
  taxSubjectId: string;
  taxSubjectKind?: 'organization' | 'individual';
  engagementId: string;
  packageId: string;
  periodId: string;
  productTrack: ProductTrack;
  rail: IdentityRail;
}

export type ConfiguredTaxObligationId = 'VAT' | 'PIT' | 'WHT' | 'CIT' | 'PAYE';
export type ObligationCapabilityState = 'AVAILABLE' | 'BLOCKED' | 'NOT_IMPLEMENTED';
export type FilingDeadlineUrgency = 'DATE_PASSED' | 'DUE_TODAY' | 'URGENT' | 'DUE_SOON' | 'COMING_UP' | 'SCHEDULED';

export interface KnownFilingDeadline {
  dueDate: string;
  daysUntilDue: number;
  urgency: FilingDeadlineUrgency;
  urgencyLabel: string;
  priority: number;
  attentionRequired: boolean;
  rule: {
    authority: string;
    title: string;
    locator: string;
    sourceUri: string;
    qualification: string;
  };
}

export interface ConfiguredTaxObligation {
  id: ConfiguredTaxObligationId;
  applicability: 'APPLICABLE';
  taxpayerRole: 'TAXPAYER' | 'WITHHOLDING_AGENT' | 'EMPLOYER';
  packageId: string;
  label: string;
  description: string;
  cadence: string;
  authorityLabel: string;
  deadline?: KnownFilingDeadline;
  capabilities: Record<'computation' | 'preparation' | 'filing' | 'payment', {
    state: ObligationCapabilityState;
    detail: string;
  }>;
}

export interface ResolvedIdentityContext {
  contextId: string;
  expiresAt: string;
  membershipId: string;
  actingCapacity: string;
  taxObligation?: ConfiguredTaxObligation;
  assignmentId?: string;
  scope: IdentityScope;
}

export interface IdentityContextsResponse {
  contexts: ResolvedIdentityContext[];
}

export interface CitPositionRequest {
  packageId: 'AMOYE-CIT-NRS-ANNUAL-ORDINARY-CAND-001';
  reviewedFactSnapshotSha256: string;
  facts: { kind: 'CIT'; taxId: string; legalName: string; grossTurnoverKobo: string;
    totalFixedAssetsKobo: string; aggregateGroupTurnoverKobo: string; accountingProfitBeforeTaxKobo: string;
    additionsKobo: string; deductionsKobo: string; broughtForwardLossKobo: string;
    otherAssessableProfitKobo: string; capitalAllowanceBroughtForwardKobo: string;
    currentCapitalAllowanceClass: 'NONE' | 'CLASS_1' | 'CLASS_2' | 'CLASS_3';
    currentCapitalAllowanceBaseKobo: string; transitionReviewReference: string; humanReviewed: true };
}

export interface CitPositionResult {
  id: string; packageId: string; packageVersion: string; packageAcceptance: 'CANDIDATE';
  outputSha256: string; currency: 'NGN'; companyClassification: 'SMALL_COMPANY' | 'STANDARD_COMPANY';
  totalAssessableProfitKobo: string; totalProfitKobo: string; citKobo: string;
  developmentLevyKobo: string; grossStatutoryAmountKobo: string; netPayableKobo: string;
  specialistReviewRequired: true; specialistReviewBlockers: string[];
  trace: Array<{ id: string; operation: string; resultKobo: string }>;
}

export type IntelligenceMode = 'EXPERIENCE_ASSISTANCE' | 'PROCESSING_EXPLANATION';
export type IntelligencePurpose = 'EXPLAIN_TAX_POSITION' | 'SUMMARIZE_REVIEWED_FACTS'
  | 'SURFACE_EXCEPTIONS' | 'GUIDE_NEXT_STEP' | 'CLASSIFY_EVIDENCE' | 'PERSIST_CONVERSATION';
export type IntelligenceReadTool = 'tax_calculation.read' | 'tax_trace.read'
  | 'reviewed_fact_snapshot.read' | 'tax_source.read' | 'return_status.read' | 'workflow_readiness.read';

export interface IntelligenceReadRequest {
  toolName: IntelligenceReadTool;
  parameters?: Record<string, unknown>;
}

export type IntelligenceConsent = { enabled: false } | { enabled: true; evidenceId: string };

export interface IntelligenceQueryRequest {
  rail: IdentityRail;
  requestId: string;
  purpose: IntelligencePurpose;
  prompt: string;
  mode?: IntelligenceMode;
  consent?: IntelligenceConsent;
  requestedReads: IntelligenceReadRequest[];
  deterministicTaxResult?: {
    calculationId: string;
    calculationSha256: string;
    traceSha256: string;
    method: 'DETERMINISTIC_RULE_ENGINE';
  };
}

export interface IntelligenceResponse {
  responseId: string;
  scope: IdentityScope;
  mode: IntelligenceMode;
  answer: string;
  citations: Array<{ sourceId: string; sourceVersion: string; sourceSha256: string; locator: string;
    title?: string; authority?: string; sourceUri?: string }>;
  authorizedReadDecisionIds: string[];
  modelId: string;
  modelVersion: string;
  generatedAt: string;
  limitations: string[];
  prohibitedEffect: 'READ_ONLY_NO_FACT_TAX_RETURN_INVOICE_SUBMISSION_OR_PAYMENT_WRITE';
}

export type AssistantHistoryMode = 'EPHEMERAL' | 'PERSISTED';
export interface AssistantNavigationIntent {
  id: string;
  kind: 'OPEN_OVERVIEW' | 'OPEN_EVIDENCE' | 'OPEN_EVIDENCE_REVIEW' | 'OPEN_RETURNS'
    | 'OPEN_RETURN_FACTS' | 'VIEW_CALCULATION_TRACE' | 'CONTINUE_TO_FILING_REVIEW'
    | 'OPEN_FILING' | 'OPEN_INVOICES' | 'OPEN_CLIENT';
  label: string;
  reason: string;
  target: { surface: 'PRACTICE' | 'BUSINESS' | 'PERSONAL'; sectionId: string;
    controlId?: string; resourceRef?: string };
  effect: 'NAVIGATE_FOCUS_ONLY';
  requiresReauthorization: true;
}
export interface AssistantTurnResponse {
  conversation: { threadId: string; turnId: string; contextKeySha256: string;
    historyMode: AssistantHistoryMode; retainedUntil?: string };
  context: { kind: 'CLIENT' | 'PORTFOLIO' } & Record<string, unknown>;
  plan: { policyVersion: 'amoye-assistant-intent-v1'; querySha256: string;
    disposition: 'PLANNED' | 'PARTIAL' | 'NEEDS_CLARIFICATION'; intents: string[]; unsupportedClauseCount: number };
  activities: Array<{ id: string; code: string; label: string; state: 'COMPLETED'; occurredAt: string }>;
  answer: string;
  citations: Array<{ sourceId: string; sourceVersion: string; sourceSha256: string; locator: string;
    title?: string; authority?: string; sourceUri?: string }>;
  navigationIntents: AssistantNavigationIntent[];
  authorizedReadDecisionIds: string[];
  limitations: string[];
  generatedAt: string;
  modelId: string;
  modelVersion: string;
  prohibitedEffect: 'READ_ONLY_NAVIGATION_ONLY_NO_REGULATED_WRITE';
}
export interface AssistantHistoryResponse {
  threadId?: string;
  contextKeySha256: string;
  turns: Array<{ sequence: number; question: string; response: AssistantTurnResponse; createdAt: string }>;
}

interface PractitionerPortfolioContextResponse { contextId: string; }
interface PractitionerClientContextResponse { contextId: string; }
interface PractitionerExplanationResponse {
  packet: { limitations: string[]; };
  generated?: IntelligenceResponse;
}
export interface EvidenceWorkflowPromotion {
  evidenceId: string;
  processingGrantId?: string;
  processingRevoked?: boolean;
  proposalIdsByTransactionId: Record<string, string[]>;
  proposalsByItemId?: Record<string, EvidenceWorkflowProposal[]>;
  duplicateOfEvidenceId?: string;
}
export interface EvidenceWorkflowProposal { id: string; fieldPath: string; proposedValue?: unknown;
  reviewDecision?: { action: 'ACCEPT_PROPOSAL' | 'REJECT_PROPOSAL' | 'CORRECT_AND_ACCEPT' | 'DEFER_BLOCKING';
    correctedValue?: unknown; recordedAt: string } }
export interface HumanEvidenceClassification {
  evidenceId: string;
  duplicateOfEvidenceId?: string;
  evidenceCategory?: string;
  ownershipState?: string;
  humanDecisionAuthoritativeForClassification: boolean;
}

export interface UploadRequest {
  client_id: string;
  filename: string;
  mime_type: string;
  content_base64: string;
  model_assistance_requested?: boolean;
  source_type?: 'api' | 'tus' | 'webhook' | 'whatsapp';
  upload_id?: string;
  job_id?: string;
  expires_at?: string;
}

export interface UploadAccepted {
  result: { status: 'accepted'; workflow: 'ingestion'; queued_job_id: string };
  envelope: Record<string, unknown>;
}

export interface ClassificationJob {
  job_id: string;
  upload_id: string;
  client_id: string;
  status: JobState;
  transaction_count: number;
  auto_classified_count: number;
  review_required_count: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  failure_code?: string;
  failure_detail?: string;
}

export interface ClassificationJobSummary extends ClassificationJob {
  filename: string;
  mime_type: string;
  size_bytes: string | null;
  content_sha256: string | null;
  document_available: boolean;
}

export interface ClassifiedTransaction {
  id: string;
  transaction_date: string;
  description: string | null;
  amount_kobo: string;
  direction: 'credit' | 'debit';
  source_platform: string | null;
  nta_code: string | null;
  vat_amount_kobo: string;
  confidence: string | null;
  review_status: ReviewState;
  classification?: {
    schema_version: string; product_surface: string; evidence_category: string; treatment_label: string;
    period_state: 'matched' | 'mismatch' | 'not_assessed'; reason_codes: string[]; rule_id: string;
  };
}

export interface ClassificationResult {
  job: ClassificationJob;
  transactions: ClassifiedTransaction[];
  document_analysis?: {
    extractor: string;
    owner_name?: string;
    tax_identifier?: string;
    ownership_confidence?: string;
    ownership_basis?: string;
    classification_schema_version?: string;
    product_surface?: string;
    document_category?: string;
    ownership_state?: 'matched' | 'mismatch' | 'needs_confirmation' | 'not_detected';
    period_state?: 'matched' | 'mismatch' | 'not_assessed';
    selected_period_id?: string;
    tax_package_id?: string;
    duplicate_of_job_id?: string;
    review_reason_codes?: string[];
  };
}

export interface EvidenceDocumentView {
  blob: Blob;
  mimeType: string;
  filename: string;
  sha256?: string;
}

export interface EvidenceActivityItem {
  id: string; sequence: number;
  event_type: 'UPLOAD_ACCEPTED' | 'DELETE_REQUESTED' | 'DELETE_RETRY_REQUESTED'
    | 'DELETE_COMPLETED' | 'DELETE_FAILED';
  outcome: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  actor_label: string; actor_capacity: string; action_label: string; detail: string;
  classification_job_id: string; batch_id: string | null; correlation_id: string;
  occurred_at: string; integrity: 'verified' | 'invalid';
}

export interface TaxCalculationResult {
  id: string;
  packageId: string;
  packageVersion: string;
  packageAcceptance: 'CANDIDATE' | 'PRACTITIONER_ACCEPTED';
  inputSnapshotSha256: string;
  inputSha256: string;
  outputSha256: string;
  currency: 'NGN';
  liabilityKobo: string;
  trace: Array<{ id: string; rule: string; operandsKobo: string[]; rateBasisPoints?: number; resultKobo: string; sourceIds: string[] }>;
  sourceCitations: Array<{ id: string; authority: string; title: string; url: string; publishedOn: string; retrievedOn: string; relevantLocator: string; sha256?: string }>;
}

export interface ReviewedSnapshotCalculationResource {
  calculationId: string;
  packageId: string;
  packageVersion: string;
  packageAcceptance: 'CANDIDATE' | 'PRACTITIONER_ACCEPTED';
  snapshotId: string;
  snapshotSha256: string;
  liabilityKobo: string;
  calculationSha256: string;
  computedAt: string;
  method: 'DETERMINISTIC_RULE_ENGINE';
}

export interface PitCalculationRequest {
  packageId: 'AMOYE-PIT-ANNUAL-NRS-SPECIAL-CAND-001' | 'AMOYE-PIT-ANNUAL-STATE-CAND-001';
  periodStart: string;
  reviewedFactSnapshotSha256: string;
  facts: { kind: 'PIT'; grossIncomeKobo: string; pensionKobo: string; nationalHousingFundKobo: string;
    nationalHealthInsuranceKobo: string; ownerOccupiedMortgageInterestKobo: string;
    lifeInsurancePremiumKobo: string; annualRentPaidKobo: string; taxCreditsKobo: string };
}
export type PersonalTaxDraftResource = { state: 'EMPTY' } | { state: 'SAVED'; version: number;
  facts: PitCalculationRequest['facts']; savedAt: string; calculation?: TaxCalculationResult };
export interface VatCalculationRequest {
  packageId: 'AMOYE-VAT-NRS-MONTHLY-CAND-001'; periodStart: string; reviewedFactSnapshotSha256: string;
  facts: { kind: 'VAT'; taxableOutputValueKobo: string; zeroRatedOutputValueKobo: string;
    exemptOutputValueKobo: string; allowableInputVatKobo: string };
}

export interface TaxReturnResource {
  id: string; version: number; track: 'A' | 'B1' | 'B2'; workspaceId: string; taxpayerId: string;
  engagementId?: string; packageId: string; periodStart: string; periodEndExclusive: string; status: string;
  factSnapshotSha256?: string; calculationSha256?: string; liabilityKobo?: string; artifactSha256?: string;
  submissionRequestId?: string; mockBridgeReference?: string;
}

export interface RegulatedPayloadResource {
  payloadId: string;
  standard: 'NRS_MBS_EINVOICE' | 'NRS_S2S_TFG' | 'PEPPOL_BIS_BILLING_3' | 'OASIS_UBL_2_1';
  documentKind: 'VAT_RETURN' | 'WHT_RETURN' | 'PERSONAL_TAX_RETURN' | 'UBL_INVOICE';
  standardVersion: string;
  mediaType: 'application/json' | 'application/xml';
  payloadBase64: string;
  payloadSha256: string;
  validation: { state: 'NOT_VALIDATED' | 'SCHEMA_VALIDATED' | 'SCHEMA_AND_RULES_VALIDATED';
    schemaId: string; schemaVersion: string; schemaSha256?: string; validatorId: string; validatorVersion: string;
    validatedAt?: string; validationEvidenceSha256?: string; unknownRequiredFields: string[] };
  createdAt: string;
}

export interface GovernmentSubmissionResource {
  id: string;
  artifactType: 'TAX_RETURN' | 'E_INVOICE';
  artifactId: string;
  artifactVersion: string;
  artifactSha256: string;
  payload: RegulatedPayloadResource;
  adapterContractId: string;
  adapterContractVersion: string;
  idempotencyKey: string;
  environment: 'mock';
  state: 'MOCK_QUEUED' | 'MOCK_RECEIVED' | 'MOCK_FAILED' | 'MOCK_DISPATCH_AMBIGUOUS' | 'MOCK_RECONCILIATION_REQUIRED';
  attempts: Array<{ id: string; attemptNumber: number; operation: string; state: string; retryable: boolean;
    failureCode?: string; startedAt: string; completedAt: string }>;
}

export interface StatutoryPaymentResource {
  id: string;
  liabilityArtifactId: string;
  liabilityArtifactSha256: string;
  amountKobo: string;
  currency: 'NGN';
  state: 'MOCK_INITIATED' | 'MOCK_PENDING' | 'MOCK_CONFIRMED_CANDIDATE' | 'MOCK_FAILED'
    | 'MOCK_AMBIGUOUS_RECONCILIATION_REQUIRED';
  environment: 'mock';
  officialTaxAuthorityEffect: 'NONE';
  requiresOfficialReconciliation: true;
  providerReference?: string;
  createdAt: string;
}

export interface ReviewedInvoicePartyInput {
  endpointId: string; endpointSchemeId: string; partyId: string; partyIdSchemeId: string; registrationName: string;
  companyId: string; companyIdSchemeId: string; taxRegistrationId: string;
  address: { streetName: string; additionalStreetName?: string; cityName: string; postalZone?: string; countryCode: string };
}

export interface ReviewedInvoiceFactsInput {
  reviewedFactSnapshotSha256: string; reviewedAt: string; invoiceNumber: string; issueDate: string; dueDate: string;
  invoiceTypeCode: '380'; currency: 'NGN'; buyerReference: string; paymentMeansCode: string;
  supplier: ReviewedInvoicePartyInput; customer: ReviewedInvoicePartyInput;
  lines: Array<{ id: string; itemName: string; quantity: string; unitCode: string; unitPriceKobo: string;
    lineExtensionAmountKobo: string; taxCategoryCode: 'S' | 'Z' | 'E'; taxRateBasisPoints: number; taxExemptionReason?: string }>;
  totals: { lineExtensionAmountKobo: string; taxExclusiveAmountKobo: string; taxAmountKobo: string;
    taxInclusiveAmountKobo: string; payableAmountKobo: string };
}

export interface EInvoiceResource {
  id: string; version: number; track: 'A' | 'B1'; workspaceId: string; taxpayerId: string; invoiceNumber: string;
  issueDate: string; payableAmountKobo: string; state: string; submissionRequestId?: string;
  engagementId?: string; packageId?: string; periodId?: string;
  regulatedPayload: { payloadSha256: string; standard: string; validation: { state: string };
    payloadBase64?: string; mediaType?: string };
}

export interface LocalInvoiceSubmission {
  id: string; invoiceId: string; artifactSha256: string;
  state: 'QUEUED' | 'RECEIVED' | 'PROCESSING' | 'ACKNOWLEDGED' | 'ACCEPTED' | 'REJECTED' | 'RESPONSE_UNKNOWN';
  submittedAt: string; updatedAt: string; receiptReference?: string; message: string; retryable: boolean;
  events: Array<{ state: LocalInvoiceSubmission['state']; occurredAt: string; message: string }>;
  nextPollAfterMs?: number;
}
export interface LocalInvoiceEnvelope {
  policy: { mode: 'LOCAL_EXPERIENCE'; officialSubmission: false };
  invoice: EInvoiceResource; submission?: LocalInvoiceSubmission; canSubmit: boolean; canRefresh: boolean;
}

export interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    correlation_id?: string;
    retryable?: boolean;
  };
  outcome?: 'DENIED';
  reason?: 'context_invalid' | 'authority_stale' | 'action_denied' | string;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly retryable: boolean;
  readonly retryAfterSeconds?: number;

  constructor(status: number, payload: ApiErrorPayload, retryAfterSeconds?: number) {
    super(payload.error?.message ?? deniedRequestMessage(payload.reason) ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.error?.code ?? payload.reason ?? 'request_failed';
    this.correlationId = payload.error?.correlation_id ?? payload.correlationId;
    this.retryable = payload.error?.retryable ?? (status === 429 || status >= 500);
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

export interface AmoyeApiOptions {
  baseUrl?: string;
  getToken?: () => string | undefined;
  fetchImpl?: typeof fetch;
  onAuthenticationRequired?: (error: ApiError) => void;
  getContextId?: (rail: IdentityRail) => string | undefined;
  getResolvedContext?: (rail: IdentityRail) => ResolvedIdentityContext | undefined;
  getResolvedContexts?: () => readonly ResolvedIdentityContext[];
  onIdentityContextsRefreshed?: (contexts: readonly ResolvedIdentityContext[]) => void;
}

export class AmoyeApi {
  private readonly baseUrl: string;
  private readonly getToken: () => string | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly onAuthenticationRequired?: (error: ApiError) => void;
  private readonly getContextId: (rail: IdentityRail) => string | undefined;
  private readonly getResolvedContext: (rail: IdentityRail) => ResolvedIdentityContext | undefined;
  private readonly getResolvedContexts: () => readonly ResolvedIdentityContext[];
  private readonly onIdentityContextsRefreshed?: (contexts: readonly ResolvedIdentityContext[]) => void;
  private readonly selectedContextIds: Partial<Record<IdentityRail, string>> = {};
  private readonly selectedResolvedContexts: Partial<Record<IdentityRail, ResolvedIdentityContext>> = {};
  private readonly classificationConsentRequests = new Map<string, { expiresAt: number; request: Promise<void> }>();
  private readonly evidencePromotionRequests = new Map<string, Promise<EvidenceWorkflowPromotion>>();
  private identityContextRefresh?: Promise<IdentityContextsResponse>;
  private authenticationFailureNotified = false;

  constructor(options: AmoyeApiOptions = {}) {
    this.baseUrl = resolveApiBaseUrl(options.baseUrl ?? import.meta.env.VITE_API_BASE_URL);
    this.getToken = options.getToken ?? defaultTokenReader;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onAuthenticationRequired = options.onAuthenticationRequired;
    this.getContextId = options.getContextId ?? (() => undefined);
    this.getResolvedContext = options.getResolvedContext ?? (() => undefined);
    this.getResolvedContexts = options.getResolvedContexts ?? (() => []);
    this.onIdentityContextsRefreshed = options.onIdentityContextsRefreshed;
  }

  /** A successful sign-in starts a new authentication lifecycle. */
  resetAuthenticationFailure(): void {
    this.authenticationFailureNotified = false;
  }

  health(signal?: AbortSignal): Promise<ServiceStatus> {
    return this.request('/health', { signal, authenticated: false });
  }

  readiness(signal?: AbortSignal): Promise<ServiceStatus> {
    return this.request('/ready', { signal, authenticated: false });
  }

  createSession(email: string, password: string, product: PersonaId, signal?: AbortSignal): Promise<AccountSession> {
    return this.request<AccountSession>('/api/auth/session', {
      method: 'POST', body: { email, password, product }, signal, authenticated: false,
    });
  }

  currentSession(accessToken: string, signal?: AbortSignal): Promise<CurrentAccountSession> {
    return this.request<CurrentAccountSession>('/api/auth/session/current', { signal, authenticated: false,
      headers: sessionHeaders(accessToken) });
  }

  accountCapabilities(): Promise<{secure_accounts:boolean;passkey_first?:boolean;mfa_enrollment_required?:boolean;message_code_mfa?:boolean}> {
    return this.request('/api/auth/capabilities',{authenticated:false});
  }
  accountRequest<T>(path:string,product:PersonaId,body?:unknown):Promise<T> {
    return this.request<T>(`/api${path}`,{authenticated:false,headers:{'x-amoye-product':product},
      ...(body!==undefined?{method:'POST',body}:{})});
  }
  logoutAccount(product:PersonaId):Promise<unknown> {return this.accountRequest('/auth/accounts/logout',product,{});}

  commercialPlans(product?:PersonaId,signal?:AbortSignal):Promise<{plans:CommercialPlan[]}>{
    return this.request(`/api/v2/commercial/plans${product?`?product=${encodeURIComponent(product)}`:''}`,
      {signal,authenticated:false});
  }
  beginCommercialAcquisition(input:{plan_id:string;email:string;display_name:string;organisation_name?:string;
    phone_e164:string;password:string},signal?:AbortSignal):Promise<CommercialPurchase>{
    return this.request('/api/v2/commercial/acquisition',{method:'POST',body:input,signal,authenticated:false});
  }
  completeCommercialAcquisition(reference:string,signal?:AbortSignal):Promise<CommercialPurchase>{
    return this.request(`/api/v2/commercial/acquisition/${encodeURIComponent(reference)}/complete`,
      {method:'POST',signal,authenticated:false});
  }
  verifyCommercialEmail(token:string,signal?:AbortSignal):Promise<CommercialPurchase>{return this.request(
    '/api/v2/commercial/acquisition/verify-email',{method:'POST',body:{token},signal,authenticated:false});}
  resendCommercialEmailVerification(reference:string,email:string,signal?:AbortSignal):Promise<CommercialPurchase>{return this.request(
    `/api/v2/commercial/acquisition/${encodeURIComponent(reference)}/resend-email-verification`,
    {method:'POST',body:{email},signal,authenticated:false});}
  remindAccountEmail(phone_e164:string,signal?:AbortSignal):Promise<{accepted:true;message:string}>{return this.request('/api/v2/commercial/account-recovery/email-reminder',
    {method:'POST',body:{phone_e164},signal,authenticated:false});}
  operationsSummary(access:OperatorAccess,signal?:AbortSignal):Promise<OperationsSummary>{return this.request('/api/v2/commercial/operations/summary',
    {signal,authenticated:false,headers:operatorHeaders(access)});}
  operationsCustomers(access:OperatorAccess,query:string,signal?:AbortSignal):Promise<{customers:CommercialCustomer[]}>{return this.request(
    `/api/v2/commercial/operations/customers?query=${encodeURIComponent(query)}`,{signal,authenticated:false,headers:operatorHeaders(access)});}
  operationsActivity(access:OperatorAccess,principalId?:string,signal?:AbortSignal):Promise<{events:OperationsEvent[]}>{return this.request(
    `/api/v2/commercial/operations/activity${principalId?`?principal_id=${encodeURIComponent(principalId)}`:''}`,
    {signal,authenticated:false,headers:operatorHeaders(access)});}
  operationsOffers(access:OperatorAccess,signal?:AbortSignal):Promise<{offers:CommercialOffer[]}>{return this.request('/api/v2/commercial/operations/offers',
    {signal,authenticated:false,headers:operatorHeaders(access)});}
  saveOperationsOffer(access:OperatorAccess,input:Omit<CommercialOffer,'id'|'created_by'|'created_at'|'updated_at'> & {id?:string},signal?:AbortSignal):Promise<CommercialOffer>{
    return this.request('/api/v2/commercial/operations/offers',{method:'POST',body:input,signal,authenticated:false,headers:operatorHeaders(access)});}
  applyOperationsOffer(access:OperatorAccess,input:{principal_id:string;offer_id:string;reason:string},signal?:AbortSignal):Promise<unknown>{return this.request(
    '/api/v2/commercial/operations/grants',{method:'POST',body:input,signal,authenticated:false,headers:operatorHeaders(access)});}
  operationsGrants(access:OperatorAccess,principalId:string,signal?:AbortSignal):Promise<{grants:CommercialGrant[]}>{return this.request(
    `/api/v2/commercial/operations/grants?principal_id=${encodeURIComponent(principalId)}`,{signal,authenticated:false,headers:operatorHeaders(access)});}
  revokeOperationsGrant(access:OperatorAccess,grantId:string,reason:string,signal?:AbortSignal):Promise<CommercialGrant>{return this.request(
    `/api/v2/commercial/operations/grants/${encodeURIComponent(grantId)}/revoke`,{method:'POST',body:{reason},signal,authenticated:false,headers:operatorHeaders(access)});}
  commercialAdminPlans(adminKey:string,signal?:AbortSignal):Promise<{plans:CommercialPlan[]}>{
    return this.request('/api/v2/commercial/admin/plans',{signal,authenticated:false,headers:{'x-amoye-admin-key':adminKey}});
  }
  saveCommercialPlan(adminKey:string,input:{id?:string;expected_version?:number;code:string;product:PersonaId;name:string;
    description:string;amount_kobo:string;interval:'MONTHLY'|'ANNUAL';state:'DRAFT'|'PUBLISHED'|'RETIRED';
    provider_plan_code?:string;capabilities:Record<string,number>},signal?:AbortSignal):Promise<CommercialPlan>{
    return this.request('/api/v2/commercial/admin/plans',{method:'POST',body:input,signal,authenticated:false,
      headers:{'x-amoye-admin-key':adminKey}});
  }

  identityContexts(signal?: AbortSignal): Promise<IdentityContextsResponse> {
    return this.request('/api/v2/identity/contexts', { signal });
  }

  createTaxSubjectSetup(input: { display_name: string; tin?: string;
    subject_kind: 'organization' | 'individual'; tax_types: Array<'VAT' | 'PIT' | 'WHT' | 'CIT' | 'PAYE'> },
  signal?: AbortSignal): Promise<CreatedTaxSubject> {
    return this.request('/api/v2/onboarding/tax-subjects', { method: 'POST', body: input, signal });
  }

  createPersonalTaxYear(taxYear: number, signal?: AbortSignal): Promise<CreatedPersonalTaxYear> {
    return this.request('/api/v2/onboarding/personal-tax-years', { method: 'POST', body: { tax_year: taxYear }, signal });
  }

  /** Allows a context-aware workspace, such as Track A client selection, to
   * bind subsequent calls on one rail without changing the login session. */
  selectIdentityContext(rail: IdentityRail, contextId?: string): void {
    delete this.selectedResolvedContexts[rail];
    if (contextId) this.selectedContextIds[rail] = contextId;
    else delete this.selectedContextIds[rail];
  }

  /** Selects both the authorization token and its immutable scope. Use this
   * when a feature changes taxpayer/client and may issue intelligence reads. */
  selectResolvedIdentityContext(context: ResolvedIdentityContext): void {
    this.selectedContextIds[context.scope.rail] = context.contextId;
    this.selectedResolvedContexts[context.scope.rail] = context;
  }

  /** Explicitly resolves an otherwise ambiguous request (for example, a
   * practitioner client with more than one package on the same rail). */
  selectContext(context: ResolvedIdentityContext): void {
    this.selectResolvedIdentityContext(context);
  }

  selectIdentityContexts(contextIds: Partial<Record<IdentityRail, string>>): void {
    this.clearIdentityContextSelection();
    if (contextIds.tax_return) this.selectedContextIds.tax_return = contextIds.tax_return;
    if (contextIds.e_invoice) this.selectedContextIds.e_invoice = contextIds.e_invoice;
  }

  clearIdentityContextSelection(): void {
    delete this.selectedContextIds.tax_return;
    delete this.selectedContextIds.e_invoice;
    delete this.selectedResolvedContexts.tax_return;
    delete this.selectedResolvedContexts.e_invoice;
  }

  async upload(body: UploadRequest, signal?: AbortSignal): Promise<UploadAccepted> {
    if (body.model_assistance_requested) await this.ensureClassificationModelConsent(signal);
    return this.request('/api/uploads', { method: 'POST', body, signal, contextRail: 'tax_return' });
  }

  private async ensureClassificationModelConsent(signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a taxpayer and return period before enabling model-assisted classification.', retryable: false } });
    const existing = this.classificationConsentRequests.get(context.contextId);
    if (existing && existing.expiresAt > Date.now() + 5_000) return existing.request;
    const startsAt = new Date(); const expiresAt = startsAt.getTime() + 30 * 60_000;
    const request = this.request('/api/v2/intelligence/consents', { method: 'POST', signal,
      contextRail: 'tax_return', contextId: context.contextId, body: { purposes: ['CLASSIFY_EVIDENCE'],
        noticeVersion: 'amoye-classification-model-assistance-v1',
        legalBasisRecordId: 'd0000000-0000-4000-8000-000000000099',
        sourceEvidenceSha256: await browserSha256('Optional model classification; review required; no tax authority.'),
        startsAt: startsAt.toISOString(), expiresAt: new Date(expiresAt).toISOString() } }).then(() => undefined);
    this.classificationConsentRequests.set(context.contextId, { expiresAt, request });
    try { await request; } catch (error) { this.classificationConsentRequests.delete(context.contextId); throw error; }
  }

  job(jobId: string, signal?: AbortSignal, contextId?: string): Promise<ClassificationJob> {
    return this.request(`/api/classification-jobs/${encodeURIComponent(jobId)}`, {
      signal, contextRail: 'tax_return', ...(contextId ? { contextId } : {}),
    });
  }

  listClassificationJobs(taxpayerId: string, signal?: AbortSignal,
    contextId?: string): Promise<{ jobs: ClassificationJobSummary[] }> {
    return this.request(`/api/classification-jobs?taxpayerId=${encodeURIComponent(taxpayerId)}`, {
      signal, contextRail: 'tax_return', ...(contextId ? { contextId } : {}),
    });
  }

  evidenceActivity(taxpayerId: string, limit = 20, signal?: AbortSignal,
    contextId?: string): Promise<{ events: EvidenceActivityItem[] }> {
    const safeLimit = Math.max(1, Math.min(100, Math.trunc(limit)));
    return this.request(`/api/classification-jobs/activity?taxpayerId=${encodeURIComponent(taxpayerId)}&limit=${safeLimit}`, {
      signal, contextRail: 'tax_return', ...(contextId ? { contextId } : {}),
    });
  }

  result(jobId: string, signal?: AbortSignal, contextId?: string): Promise<ClassificationResult> {
    return this.request(`/api/classification-jobs/${encodeURIComponent(jobId)}/result`, {
      signal, contextRail: 'tax_return', ...(contextId ? { contextId } : {}),
    });
  }

  async promoteClassificationEvidence(file: File, jobId: string,
    signal?: AbortSignal, resolvedContext?: ResolvedIdentityContext): Promise<EvidenceWorkflowPromotion> {
    return this.promoteClassificationSource({ filename: file.name, byteLength: file.size.toString(),
      sha256: await fileSha256(file), format: evidenceMediaType(file) }, jobId, signal, resolvedContext);
  }

  async resumeClassificationEvidence(job: ClassificationJobSummary,
    signal?: AbortSignal, resolvedContext?: ResolvedIdentityContext): Promise<EvidenceWorkflowPromotion> {
    if (!job.content_sha256 || job.size_bytes === null || !job.document_available) {
      throw new ApiError(409, { error: { code: 'evidence_source_unavailable',
        message: 'The secured source is no longer available for processing.', retryable: false } });
    }
    return this.promoteClassificationSource({ filename: job.filename, byteLength: job.size_bytes,
      sha256: job.content_sha256, format: classificationJobEvidenceFormat(job) }, job.job_id, signal,
    resolvedContext);
  }

  async prepareClassificationEvidenceForHumanReview(source: File | ClassificationJobSummary, jobId: string,
    signal?: AbortSignal, resolvedContext?: ResolvedIdentityContext): Promise<EvidenceWorkflowPromotion> {
    const metadata = source instanceof File
      ? { filename: source.name, byteLength: source.size.toString(), sha256: await fileSha256(source),
        format: evidenceMediaType(source) }
      : { filename: source.filename, byteLength: source.size_bytes ?? '', sha256: source.content_sha256 ?? '',
        format: classificationJobEvidenceFormat(source) };
    if (!metadata.byteLength || !metadata.sha256 || (!(source instanceof File) && !source.document_available)) {
      throw new ApiError(409, { error: { code: 'evidence_source_unavailable',
        message: 'The secured source is no longer available for human review.', retryable: false } });
    }
    return this.promoteClassificationSource(metadata, jobId, signal, resolvedContext, false);
  }

  private promoteClassificationSource(source: { filename: string; byteLength: string; sha256: string; format: string },
    jobId: string, signal?: AbortSignal, resolvedContext?: ResolvedIdentityContext,
    extract = true): Promise<EvidenceWorkflowPromotion> {
    const context = resolvedContext ?? this.resolvedContextForRail('tax_return');
    if (!context) return Promise.reject(new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before promoting reviewed evidence.', retryable: false } }));
    const cacheKey = `${context.contextId}:${jobId}:${extract ? 'extract' : 'human-review'}`;
    const inFlight = this.evidencePromotionRequests.get(cacheKey);
    if (inFlight) return inFlight;
    const request = this.performClassificationPromotion(context, source, jobId, signal, extract)
      .finally(() => { if (this.evidencePromotionRequests.get(cacheKey) === request) {
        this.evidencePromotionRequests.delete(cacheKey);
      } });
    this.evidencePromotionRequests.set(cacheKey, request);
    return request;
  }

  private async performClassificationPromotion(context: ResolvedIdentityContext,
    source: { filename: string; byteLength: string; sha256: string; format: string },
    jobId: string, signal?: AbortSignal, extract = true): Promise<EvidenceWorkflowPromotion> {
    const period = periodRange(context.scope.periodId);
    const evidence = await this.request<{ id: string; version: number; custodyState: string; duplicateOfEvidenceId?: string }>('/api/v2/evidence', { method: 'POST', signal,
      contextRail: 'tax_return', contextId: context.contextId, body: { ...period, source: { filename: source.filename,
        byteLength: source.byteLength, sha256: source.sha256, stagedContentId: jobId, format: source.format,
        formatVersion: '1', channel: 'WEB_UPLOAD' }, idempotencyKey: `ui-evidence:${jobId}` } });
    if (evidence.custodyState === 'QUARANTINED' || evidence.custodyState === 'SCANNING') {
      await this.request(`/api/v2/evidence/${encodeURIComponent(evidence.id)}/scan`, { method: 'POST', signal,
        contextRail: 'tax_return', contextId: context.contextId,
        body: { result: 'CLEAN', expectedVersion: evidence.version } });
    } else if (evidence.custodyState !== 'CLEAN') {
      throw new ApiError(409, { error: { code: 'evidence_not_clean',
        message: 'The source did not pass evidence security checks.', retryable: false } });
    }
    if (!extract) return { evidenceId: evidence.id, proposalIdsByTransactionId: {}, proposalsByItemId: {},
      ...(evidence.duplicateOfEvidenceId ? { duplicateOfEvidenceId: evidence.duplicateOfEvidenceId } : {}) };
    if (evidence.duplicateOfEvidenceId) return { evidenceId: evidence.id,
      proposalIdsByTransactionId: {}, proposalsByItemId: {}, duplicateOfEvidenceId: evidence.duplicateOfEvidenceId };
    const serviceId = 'completed-classification-workflow'; const workPurpose = 'review-tax-evidence';
    const grant = await this.request<{ id: string; revokedAt?: string }>('/api/v2/evidence/processing-grants', { method: 'POST', signal,
      contextRail: 'tax_return', contextId: context.contextId, body: { ...period, evidenceIds: [evidence.id],
        granteeServiceId: serviceId, workPurpose, purposes: ['PARSE_SOURCE'], sourceEvidenceSha256: source.sha256,
        idempotencyKey: `ui-processing-grant:${jobId}` } });
    if (grant.revokedAt) return { evidenceId: evidence.id, processingGrantId: grant.id,
      processingRevoked: true, proposalIdsByTransactionId: {}, proposalsByItemId: {} };
    const extracted = await this.request<{ proposals: EvidenceWorkflowProposal[] }>(
      `/api/v2/evidence/${encodeURIComponent(evidence.id)}/extract`, { method: 'POST', signal,
        contextRail: 'tax_return', contextId: context.contextId, body: { grantId: grant.id, serviceId, workPurpose,
          idempotencyKey: `ui-extract:${jobId}` } });
    const proposalIdsByTransactionId: Record<string, string[]> = {};
    const proposalsByItemId: Record<string, EvidenceWorkflowProposal[]> = {};
    for (const proposal of extracted.proposals) {
      const itemId = /^(?:transactions|documents)\.([^.]+)\./.exec(proposal.fieldPath)?.[1];
      if (!itemId) continue;
      (proposalIdsByTransactionId[itemId] ??= []).push(proposal.id);
      (proposalsByItemId[itemId] ??= []).push(proposal);
    }
    return { evidenceId: evidence.id, processingGrantId: grant.id, proposalIdsByTransactionId, proposalsByItemId,
      ...(evidence.duplicateOfEvidenceId ? { duplicateOfEvidenceId: evidence.duplicateOfEvidenceId } : {}) };
  }

  async reviewEvidenceProposals(proposalIds: readonly string[], decision: 'accept' | 'exclude' | 'defer',
    signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before reviewing evidence.', retryable: false } });
    const action = decision === 'accept' ? 'ACCEPT_PROPOSAL'
      : decision === 'exclude' ? 'REJECT_PROPOSAL' : 'DEFER_BLOCKING';
    await Promise.all(proposalIds.map((proposalId) => this.request(
      `/api/v2/reconciliation/proposals/${encodeURIComponent(proposalId)}/review`, { method: 'POST', signal,
        contextRail: 'tax_return', contextId: context.contextId, body: { action,
          reasonCode: decision === 'accept' ? 'PRACTITIONER_SOURCE_CONFIRMED'
            : decision === 'exclude' ? 'PRACTITIONER_EXCLUDED' : 'PRACTITIONER_DEFERRED',
          idempotencyKey: `ui-review:${proposalId}:${decision}` } })));
  }

  async correctEvidenceProposals(proposals: readonly EvidenceWorkflowProposal[], correction: {
    ntaCode?: string; vatAmountKobo?: string; evidenceCategory?: string;
  }, signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before correcting evidence.', retryable: false } });
    if (proposals.length === 0) throw new Error('This item is not linked to a durable correction record.');
    const corrections = proposals.map((proposal) => ({ proposal,
      correctedValue: correctedCanonicalValue(proposal.fieldPath, correction) }))
      .filter((candidate) => candidate.correctedValue !== undefined);
    if (corrections.length === 0) throw new Error('The selected field is not linked to a durable correction record. Reprocess the source before classifying it.');
    await Promise.all(corrections.map(async ({ proposal, correctedValue }) => {
      return this.request(`/api/v2/reconciliation/proposals/${encodeURIComponent(proposal.id)}/review`, {
        method: 'POST', signal, contextRail: 'tax_return', contextId: context.contextId,
        body: { action: 'CORRECT_AND_ACCEPT', reasonCode: 'USER_CORRECTED_CLASSIFICATION', correctedValue,
          idempotencyKey: `ui-review:${proposal.id}:correct:${await browserSha256(JSON.stringify(correctedValue ?? proposal.proposedValue ?? null))}` },
      });
    }));
  }

  async reviewEvidenceOwnershipProposals(proposals: readonly EvidenceWorkflowProposal[],
    decision: 'confirm-selected-taxpayer' | 'flag-wrong-taxpayer', signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before reviewing evidence ownership.', retryable: false } });
    const ownership = proposals.filter((proposal) => proposal.fieldPath.endsWith('.ownership_state'));
    if (ownership.length === 0) throw new Error('This source has no durable ownership proposal to review.');
    await Promise.all(ownership.map((proposal) => this.request(
      `/api/v2/reconciliation/proposals/${encodeURIComponent(proposal.id)}/review`, { method: 'POST', signal,
        contextRail: 'tax_return', contextId: context.contextId,
        body: decision === 'confirm-selected-taxpayer'
          ? { action: 'CORRECT_AND_ACCEPT', reasonCode: 'USER_CONFIRMED_SELECTED_TAXPAYER', correctedValue: {
            type: 'enum', schemaId: 'AMOYE-EVIDENCE-OWNERSHIP-STATE-V2', value: 'confirmed_selected_taxpayer' },
            idempotencyKey: `ui-ownership:${proposal.id}:confirmed-selected-taxpayer` }
      : { action: 'REJECT_PROPOSAL', reasonCode: 'USER_FLAGGED_WRONG_TAXPAYER',
            idempotencyKey: `ui-ownership:${proposal.id}:wrong-taxpayer` } })));
  }

  async humanClassifyEvidence(evidenceId: string, input: { evidenceCategory: string;
    reasonCode: 'AUTOMATED_CLASSIFICATION_DISAGREED' | 'EXTRACTION_UNAVAILABLE' | 'DUPLICATE_SOURCE_DISTINCT_USE';
    reviewedAt: string; notes?: string }, signal?: AbortSignal,
  resolvedContext?: ResolvedIdentityContext): Promise<HumanEvidenceClassification> {
    const context = resolvedContext ?? this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before classifying evidence.', retryable: false } });
    const category = input.evidenceCategory.trim().toLowerCase();
    if (!EVIDENCE_CATEGORY_VALUES.has(category)) throw new Error('Choose a supported evidence category.');
    return this.request(`/api/v2/evidence/${encodeURIComponent(evidenceId)}/human-classification`, {
      method: 'POST', signal, contextRail: 'tax_return', contextId: context.contextId,
      body: { ...periodRange(context.scope.periodId), evidenceCategory: category,
        confirmSelectedTaxpayer: true, reasonCode: input.reasonCode,
        attestation: { statementVersion: 'amoye-human-evidence-classification-v1',
          reviewedAt: input.reviewedAt, sourceDocumentReviewed: true,
          automatedAssessmentNotAuthoritative: true, ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}) },
        idempotencyKey: `ui-human-classification:${evidenceId}:${category}:${input.reviewedAt}` },
    });
  }

  async readHumanEvidenceClassification(evidenceId: string, signal?: AbortSignal,
    resolvedContext?: ResolvedIdentityContext): Promise<HumanEvidenceClassification> {
    const context = resolvedContext ?? this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before reading evidence classification.', retryable: false } });
    const query = new URLSearchParams(periodRange(context.scope.periodId));
    return this.request(`/api/v2/evidence/${encodeURIComponent(evidenceId)}/human-classification?${query}`, {
      signal, contextRail: 'tax_return', contextId: context.contextId,
    });
  }

  async revokeEvidenceProcessing(grantId: string, evidenceId: string, signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a tax-return context before unlinking evidence.', retryable: false } });
    await this.request(`/api/v2/evidence/processing-grants/${encodeURIComponent(grantId)}/revoke`, { method: 'POST',
      signal, contextRail: 'tax_return', contextId: context.contextId,
      body: { revocationEvidenceSha256: await browserSha256(`unlink:${evidenceId}:${grantId}`) } });
  }

  async deleteClassificationEvidence(jobId: string, reason: 'wrong-taxpayer' | 'wrong-period'
    | 'wrong-transaction' | 'unrelated' | 'duplicate', batchId?: string, signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a taxpayer and filing period before deleting evidence.', retryable: false } });
    await this.request(`/api/classification-jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE', signal,
      contextRail: 'tax_return', contextId: context.contextId,
      body: { reason, ...(batchId ? { batch_id: batchId } : {}) } });
  }

  async evidenceDocument(jobId: string, signal?: AbortSignal): Promise<EvidenceDocumentView> {
    const token = this.getToken();
    const path = `/api/classification-jobs/${encodeURIComponent(jobId)}/document`;
    const headers: Record<string, string> = { Accept: 'application/octet-stream' };
    if (token) Object.assign(headers,sessionHeaders(token));
    const contextId = this.contextIdForRequest('tax_return', path, undefined);
    if (contextId) headers['x-amoye-context-id'] = contextId;
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, { headers, ...(signal ? { signal } : {}) });
    if (!response.ok) throw new ApiError(response.status, await readJson(response) as ApiErrorPayload,
      retryAfterSeconds(response));
    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'evidence';
    return { blob: await response.blob(), mimeType: response.headers.get('content-type')?.split(';')[0] ?? 'application/octet-stream',
      filename, ...(response.headers.get('x-amoye-content-sha256') ? { sha256: response.headers.get('x-amoye-content-sha256')! } : {}) };
  }

  calculatePersonalTax(body: PitCalculationRequest, signal?: AbortSignal): Promise<TaxCalculationResult> {
    return this.request('/api/v2/tax-position/calculate', { method: 'POST', body, signal });
  }

  loadPersonalTaxDraft(periodStart: string, periodEndExclusive: string,
    signal?: AbortSignal): Promise<PersonalTaxDraftResource> {
    const query = new URLSearchParams({ periodStart, periodEndExclusive });
    return this.request(`/api/v2/tax-position/draft?${query.toString()}`, {
      signal, contextRail: 'tax_return',
    });
  }

  savePersonalTaxDraft(body: { periodStart: string; periodEndExclusive: string;
    facts: PitCalculationRequest['facts'] }, signal?: AbortSignal): Promise<PersonalTaxDraftResource> {
    return this.request('/api/v2/tax-position/draft', {
      method: 'POST', body, signal, contextRail: 'tax_return',
    });
  }

  calculatePersonalTaxDraft(periodStart: string, periodEndExclusive: string,
    signal?: AbortSignal): Promise<TaxCalculationResult> {
    return this.request('/api/v2/tax-position/draft/calculate', {
      method: 'POST', body: { periodStart, periodEndExclusive }, signal, contextRail: 'tax_return',
    });
  }

  loadPersonalFilingProfile(contextId: string, signal?: AbortSignal): Promise<PersonalFilingProfileResponse> {
    return this.request('/api/v2/filing-profile', { signal, contextId, contextRail: 'tax_return' });
  }

  savePersonalFilingProfile(contextId: string, body: { expectedVersion: number; profile: PersonalFilingProfile; confirmResidencyReview?: boolean },
    signal?: AbortSignal): Promise<PersonalFilingProfileResponse> {
    return this.request('/api/v2/filing-profile', { method: 'PUT', body, signal, contextId, contextRail: 'tax_return' });
  }

  loadPersonalFiling(contextId: string, signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing', { signal, contextId, contextRail: 'tax_return' });
  }

  submitPersonalFiling(contextId: string, body: { preparationId: string; artifactSha256: string },
    signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/submit', { method: 'POST', body, signal, contextId, contextRail: 'tax_return' });
  }

  refreshPersonalSubmission(contextId: string, submissionId: string,
    signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/refresh', { method: 'POST', body: { submissionId }, signal,
      contextId, contextRail: 'tax_return' });
  }

  retryPersonalSubmission(contextId: string, submissionId: string,
    signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/retry', { method: 'POST', body: { submissionId }, signal,
      contextId, contextRail: 'tax_return' });
  }

  reportPersonalPortalSubmission(contextId: string,
    body: import('./personal-filing-types').PersonalPortalReportRequest,
    signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/portal-report', { method: 'POST', body, signal,
      contextId, contextRail: 'tax_return' });
  }

  exportPersonalFiling(contextId: string, preparationId: string, artifactSha256: string, signal?: AbortSignal): Promise<{
    filename: string; mimeType: string; artifactSha256: string; sha256: string; contentBase64: string;
  }> {
    return this.request(`/api/v2/personal-filing/${encodeURIComponent(preparationId)}/export?artifactSha256=${encodeURIComponent(artifactSha256)}`,
      { contextId, contextRail: 'tax_return', signal });
  }

  preparePersonalFiling(contextId: string, calculationId: string, signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/prepare', { method: 'POST', body: { calculationId },
      signal, contextId, contextRail: 'tax_return' });
  }

  signPersonalFiling(contextId: string, body: { preparationId: string; expectedVersion: number;
    artifactSha256: string; declarationSha256: string; confirmed: true }, signal?: AbortSignal): Promise<LocalPersonalFilingResponse> {
    return this.request('/api/v2/personal-filing/sign', { method: 'POST', body, signal, contextId, contextRail: 'tax_return' });
  }

  calculateVat(body: VatCalculationRequest, signal?: AbortSignal): Promise<TaxCalculationResult> {
    return this.request('/api/v2/tax-position/calculate', { method: 'POST', body, signal });
  }

  calculateCit(body: CitPositionRequest, signal?: AbortSignal): Promise<CitPositionResult> {
    return this.request('/api/v2/cit-position/calculate', { method: 'POST', body, signal, contextRail: 'tax_return' });
  }

  calculateReviewedSnapshot(body: { snapshotId: string; snapshotSha256: string; periodStart: string },
    signal?: AbortSignal): Promise<ReviewedSnapshotCalculationResource> {
    return this.request('/api/v2/tax-engine/calculations', {
      method: 'POST', body, signal, contextRail: 'tax_return',
    });
  }

  createPersonalReturn(body: { taxpayerId: string; packageId: PitCalculationRequest['packageId'];
    periodStart: string; periodEndExclusive: string }, signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request('/api/v2/tax-returns', { method: 'POST', signal,
      body: { ...body, track: 'B2' } });
  }

  createTaxReturn(body: { track: 'A' | 'B1' | 'B2'; taxpayerId: string; engagementId?: string;
    packageId: 'AMOYE-VAT-NRS-MONTHLY-CAND-001' | PitCalculationRequest['packageId'];
    periodStart: string; periodEndExclusive: string }, signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request('/api/v2/tax-returns', { method: 'POST', body, signal });
  }

  listTaxReturns(taxpayerId?: string, signal?: AbortSignal): Promise<{ taxReturns: TaxReturnResource[] }> {
    const query = taxpayerId ? `?taxpayerId=${encodeURIComponent(taxpayerId)}` : '';
    return this.request(`/api/v2/tax-returns${query}`, { signal });
  }

  attachReturnFacts(id: string, sha256: string, signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request(`/api/v2/tax-returns/${encodeURIComponent(id)}/facts`, { method: 'POST', body: { sha256 }, signal });
  }

  attachReturnCalculation(id: string, body: { calculationId: string }, signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request(`/api/v2/tax-returns/${encodeURIComponent(id)}/calculation`, { method: 'POST', body, signal });
  }

  attachReturnFilingPayload(id: string, body: RegulatedPayloadResource,
    signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request(`/api/v2/tax-returns/${encodeURIComponent(id)}/filing-payload`, {
      method: 'POST', body, signal, contextRail: 'tax_return',
    });
  }

  mutateReturn(id: string, action: 'prepare' | 'review' | 'approve' | 'mock-submission', capacityId: string,
    signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request(`/api/v2/tax-returns/${encodeURIComponent(id)}/${action}`, {
      method: 'POST', body: { capacityId }, signal,
    });
  }

  signReturn(id: string, declarationVersion: string, capacityId: string, signal?: AbortSignal): Promise<TaxReturnResource> {
    return this.request(`/api/v2/tax-returns/${encodeURIComponent(id)}/sign`, {
      method: 'POST', body: { declarationVersion, capacityId }, signal,
    });
  }

  createGovernmentSubmission(body: { rail: IdentityRail; artifactType: 'TAX_RETURN' | 'E_INVOICE'; artifactId: string;
    artifactVersion: string; artifactSha256: string; payload: RegulatedPayloadResource; adapterContractId: string;
    adapterContractVersion: string; idempotencyKey: string; stepUpEvidenceId?: string },
  signal?: AbortSignal): Promise<GovernmentSubmissionResource> {
    const { rail, ...requestBody } = body;
    return this.request('/api/v2/government/submissions', {
      method: 'POST', body: requestBody, signal, contextRail: rail,
    });
  }

  dispatchGovernmentSubmission(id: string, rail: IdentityRail, behavior: 'receive' | 'ambiguous' = 'receive',
    signal?: AbortSignal): Promise<GovernmentSubmissionResource> {
    return this.request(`/api/v2/government/submissions/${encodeURIComponent(id)}/dispatch`, {
      method: 'POST', body: { behavior }, signal, contextRail: rail,
    });
  }

  reconcileGovernmentSubmission(id: string, rail: IdentityRail,
    signal?: AbortSignal): Promise<GovernmentSubmissionResource> {
    return this.request(`/api/v2/government/submissions/${encodeURIComponent(id)}/reconcile`, {
      method: 'POST', signal, contextRail: rail,
    });
  }

  createStatutoryPayment(body: { liabilityArtifactId: string; liabilityArtifactSha256: string; amountKobo: string;
    currency: 'NGN'; idempotencyKey: string }, signal?: AbortSignal): Promise<StatutoryPaymentResource> {
    return this.request('/api/v2/government/statutory-payments', {
      method: 'POST', body, signal, contextRail: 'tax_return',
    });
  }

  reconcileStatutoryPayment(id: string, signal?: AbortSignal): Promise<StatutoryPaymentResource> {
    return this.request(`/api/v2/government/statutory-payments/${encodeURIComponent(id)}/reconcile`, {
      method: 'POST', signal, contextRail: 'tax_return',
    });
  }

  createInvoiceFromReviewedFacts(body: { track: 'A' | 'B1'; taxpayerId: string; facts: ReviewedInvoiceFactsInput },
    signal?: AbortSignal): Promise<EInvoiceResource> {
    return this.request('/api/v2/e-invoices/from-reviewed-facts', { method: 'POST', body, signal });
  }

  listInvoices(taxpayerId?: string, signal?: AbortSignal, contextId?: string): Promise<{ invoices: EInvoiceResource[] }> {
    const query = taxpayerId ? `?taxpayerId=${encodeURIComponent(taxpayerId)}` : '';
    return this.request(`/api/v2/e-invoices${query}`, { signal, contextId, contextRail: 'e_invoice' });
  }

  getLocalInvoice(id: string, contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}`, { contextId, contextRail: 'e_invoice', signal });
  }

  reviewLocalInvoice(id: string, body: { expectedVersion: number; payloadSha256: string; confirmed: true },
    contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}/review`,
      { method: 'POST', body, contextId, contextRail: 'e_invoice', signal });
  }

  approveLocalInvoice(id: string, body: { expectedVersion: number; payloadSha256: string; confirmed: true },
    contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body, contextId, contextRail: 'e_invoice', signal });
  }

  submitLocalInvoice(id: string, body: { expectedVersion: number; payloadSha256: string; confirmed: true },
    contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}/submit`,
      { method: 'POST', body, contextId, contextRail: 'e_invoice', signal });
  }

  refreshLocalInvoice(id: string, contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}/refresh`,
      { method: 'POST', body: {}, contextId, contextRail: 'e_invoice', signal });
  }

  retryLocalInvoice(id: string, contextId: string, signal?: AbortSignal): Promise<LocalInvoiceEnvelope> {
    return this.request(`/api/v2/local-e-invoices/${encodeURIComponent(id)}/retry`,
      { method: 'POST', body: {}, contextId, contextRail: 'e_invoice', signal });
  }

  mutateInvoice(id: string, action: 'prepare' | 'review' | 'approve', capacityId: string,
    signal?: AbortSignal): Promise<EInvoiceResource> {
    return this.request(`/api/v2/e-invoices/${encodeURIComponent(id)}/${action}`, {
      method: 'POST', body: { capacityId }, signal,
    });
  }

  dispatchInvoiceMock(id: string, body: { capacityId: string; idempotencyKey: string;
    adapterContractId?: string; adapterContractVersion?: string; behavior?: 'receive' | 'ambiguous' }, signal?: AbortSignal): Promise<{
      invoice: EInvoiceResource; bridgeReceipt: { state: 'MOCK_RECEIVED' | 'MOCK_REJECTED' | 'MOCK_DISPATCH_AMBIGUOUS';
        regulatedPayloadSha256: string; validationErrors: string[] } }> {
    return this.request(`/api/v2/e-invoices/${encodeURIComponent(id)}/mock-submission`, { method: 'POST', signal,
      body: { adapterContractId: body.adapterContractId ?? 'mock-app-si-bridge',
        adapterContractVersion: body.adapterContractVersion ?? '1.0.0', ...body } });
  }

  queryIntelligence(body: IntelligenceQueryRequest, signal?: AbortSignal): Promise<IntelligenceResponse> {
    const context = this.resolvedContextForRail(body.rail);
    if (!context) {
      throw new ApiError(409, { error: { code: 'identity_context_required',
        message: 'Select a resolved identity context before requesting intelligence.', retryable: false } });
    }
    return this.request('/api/v2/platform/intelligence/query', { method: 'POST', signal, contextRail: body.rail,
      contextId: context.contextId,
      body: {
        request_id: body.requestId,
        resolved_context_id: context.contextId,
        scope: context.scope,
        mode: body.mode ?? 'EXPERIENCE_ASSISTANCE',
        purpose: body.purpose,
        prompt: body.prompt,
        ...(body.consent?.enabled ? { consent: { required: true, evidence_id: body.consent.evidenceId } } : {}),
        requested_reads: body.requestedReads.map((read) => ({ tool_name: read.toolName,
          ...(read.parameters ? { parameters: read.parameters } : {}) })),
        ...(body.deterministicTaxResult ? { deterministic_tax_result: body.deterministicTaxResult } : {}),
      } });
  }

  /** Canonical consent-backed intelligence path. Unlike the retained local
   * platform presenter route, this reads the selected taxpayer's authorised
   * calculation/draft resources and uses the deployment ModelGateway. */
  async queryGroundedIntelligence(body: IntelligenceQueryRequest,
    signal?: AbortSignal): Promise<IntelligenceResponse> {
    const context = this.resolvedContextForRail(body.rail);
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a resolved identity context before requesting intelligence.', retryable: false } });
    if (!body.consent?.enabled) throw new ApiError(403, { error: { code: 'consent_required',
      message: 'Allow read-only use for this question before asking Amoye.', retryable: false } });
    const startsAt = new Date(); const expiresAt = new Date(startsAt.getTime() + 5 * 60_000);
    await this.request('/api/v2/intelligence/consents', { method: 'POST', signal, contextRail: body.rail,
      contextId: context.contextId, body: { purposes: [body.purpose],
        noticeVersion: 'amoye-read-only-intelligence-v1',
        legalBasisRecordId: 'd0000000-0000-4000-8000-000000000099',
        sourceEvidenceSha256: await browserSha256(body.consent.evidenceId),
        startsAt: startsAt.toISOString(), expiresAt: expiresAt.toISOString() } });
    return this.request('/api/v2/intelligence/responses', { method: 'POST', signal,
      contextRail: body.rail, contextId: context.contextId, body: {
        mode: body.mode ?? 'EXPERIENCE_ASSISTANCE', purpose: body.purpose, prompt: body.prompt,
        requestedReads: body.requestedReads.map((read) => ({ toolName: read.toolName,
          parameters: read.parameters ?? {} })),
        ...(body.deterministicTaxResult ? { deterministicTaxResult: body.deterministicTaxResult } : {}),
      } });
  }

  async loadHomeSuggestions(input: { rail: IdentityRail }, signal?: AbortSignal): Promise<HomeSuggestionsResponse> {
    const context = this.resolvedContextForRail(input.rail);
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a resolved context before reading Home suggestions.', retryable: false } });
    return this.request('/api/v2/intelligence/home-suggestions', { signal, contextRail: input.rail, contextId: context.contextId });
  }

  async queryAssistantIntelligence(input: { rail: IdentityRail; prompt: string; consentEvidenceId: string;
    historyMode: AssistantHistoryMode; contextKind?: 'CLIENT' | 'PORTFOLIO' },
    signal?: AbortSignal): Promise<AssistantTurnResponse> {
    const context = this.resolvedContextForRail(input.rail);
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a resolved context before requesting intelligence.', retryable: false } });
    const contextKind = input.contextKind ?? 'CLIENT';
    const purposes: IntelligencePurpose[] = [contextKind === 'PORTFOLIO' ? 'SURFACE_EXCEPTIONS' : 'GUIDE_NEXT_STEP'];
    if (input.historyMode === 'PERSISTED') purposes.push('PERSIST_CONVERSATION');
    await this.captureIntelligenceConsent(context, input.rail, input.consentEvidenceId, purposes, signal);
    const endpoint = contextKind === 'PORTFOLIO'
      ? '/api/v2/practice/intelligence/assistant/turns' : '/api/v2/intelligence/assistant/turns';
    return this.request(endpoint, { method: 'POST', signal, contextRail: input.rail,
      contextId: context.contextId, body: { prompt: input.prompt, historyMode: input.historyMode } });
  }

  async loadAssistantHistory(input: { rail: IdentityRail; consentEvidenceId: string;
    contextKind?: 'CLIENT' | 'PORTFOLIO' }, signal?: AbortSignal): Promise<AssistantHistoryResponse> {
    const context = this.resolvedContextForRail(input.rail);
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a resolved context before reading conversation history.', retryable: false } });
    const contextKind = input.contextKind ?? 'CLIENT';
    await this.captureIntelligenceConsent(context, input.rail, input.consentEvidenceId,
      ['PERSIST_CONVERSATION'], signal);
    const base = contextKind === 'PORTFOLIO' ? '/api/v2/practice/intelligence/assistant/history'
      : '/api/v2/intelligence/assistant/history';
    return this.request(`${base}?contextKind=${contextKind}`, { signal, contextRail: input.rail,
      contextId: context.contextId });
  }

  async deleteAssistantHistory(input: { rail: IdentityRail; consentEvidenceId: string;
    contextKind?: 'CLIENT' | 'PORTFOLIO' }, signal?: AbortSignal): Promise<void> {
    const context = this.resolvedContextForRail(input.rail);
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a resolved context before deleting conversation history.', retryable: false } });
    const contextKind = input.contextKind ?? 'CLIENT';
    // Deletion is a data-subject control, not a new persistence purpose. The
    // current authenticated context still scopes the request server-side.
    void input.consentEvidenceId;
    const base = contextKind === 'PORTFOLIO' ? '/api/v2/practice/intelligence/assistant/history'
      : '/api/v2/intelligence/assistant/history';
    await this.request(`${base}?contextKind=${contextKind}`, { method: 'DELETE', signal,
      contextRail: input.rail, contextId: context.contextId });
  }

  private async captureIntelligenceConsent(context: ResolvedIdentityContext, rail: IdentityRail,
    consentEvidenceId: string, purposes: IntelligencePurpose[], signal?: AbortSignal): Promise<void> {
    const startsAt = new Date(); const expiresAt = new Date(startsAt.getTime() + 5 * 60_000);
    await this.request('/api/v2/intelligence/consents', { method: 'POST', signal, contextRail: rail,
      contextId: context.contextId, body: { purposes,
        noticeVersion: 'amoye-read-only-intelligence-v2',
        legalBasisRecordId: 'd0000000-0000-4000-8000-000000000099',
        sourceEvidenceSha256: await browserSha256(consentEvidenceId),
        startsAt: startsAt.toISOString(), expiresAt: expiresAt.toISOString() } });
  }

  /** Practitioner-only selected-client RAG flow. Scope, assignment, retrieval
   * materials and model route are all resolved by the server. */
  async queryPractitionerIntelligence(prompt: string, consentEvidenceId: string,
    signal?: AbortSignal): Promise<IntelligenceResponse> {
    const context = this.resolvedContextForRail('tax_return');
    if (!context) throw new ApiError(409, { error: { code: 'identity_context_required',
      message: 'Select a client and tax-return context before requesting intelligence.', retryable: false } });
    const startsAt = new Date(); const expiresAt = new Date(startsAt.getTime() + 5 * 60_000);
    await this.request('/api/v2/intelligence/consents', { method: 'POST', signal, contextRail: 'tax_return',
      contextId: context.contextId, body: { purposes: ['EXPLAIN_TAX_POSITION', 'SURFACE_EXCEPTIONS'],
        noticeVersion: 'amoye-external-model-synthetic-test-v1',
        legalBasisRecordId: 'd0000000-0000-4000-8000-000000000099',
        sourceEvidenceSha256: await browserSha256(consentEvidenceId), startsAt: startsAt.toISOString(),
        expiresAt: expiresAt.toISOString() } });
    const portfolio = await this.request<PractitionerPortfolioContextResponse>(
      '/api/v2/practice/intelligence/portfolio-contexts', { method: 'POST', signal, contextRail: 'tax_return',
        contextId: context.contextId, body: {} });
    const client = await this.request<PractitionerClientContextResponse>(
      `/api/v2/practice/intelligence/clients/${encodeURIComponent(context.scope.taxSubjectId)}/open`,
      { method: 'POST', signal, contextRail: 'tax_return', contextId: context.contextId,
        body: { portfolioContextId: portfolio.contextId } });
    const result = await this.request<PractitionerExplanationResponse>(
      `/api/v2/practice/intelligence/client-contexts/${encodeURIComponent(client.contextId)}/explain`,
      { method: 'POST', signal, contextRail: 'tax_return', contextId: context.contextId, body: { question: prompt } });
    if (!result.generated) throw new ApiError(503, { error: { code: 'grounded_generation_unavailable',
      message: 'Amoye found the authorised records, but the configured model did not return a validated answer. Please try again.',
      retryable: true } });
    return result.generated;
  }

  async waitForJob(jobId: string, options: { signal?: AbortSignal; intervalMs?: number;
    timeoutMs?: number; contextId?: string } = {}): Promise<ClassificationJob> {
    const intervalMs = options.intervalMs ?? 1_500;
    // OCR is deliberately asynchronous and a multi-document batch can queue
    // behind other work. A one-minute browser deadline incorrectly labelled
    // durable jobs as failed even though the worker subsequently completed
    // them. Keep the bounded wait, but size it for the supported batch path.
    const timeoutMs = options.timeoutMs ?? 2 * 60_000;
    if (!Number.isFinite(intervalMs) || intervalMs < 1 || intervalMs > 60_000) throw new RangeError('Polling interval must be between 1 and 60000 milliseconds.');
    if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 900_000) throw new RangeError('Polling timeout must be between 1 and 900000 milliseconds.');
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', abortFromCaller, { once: true });
    if (options.signal?.aborted) abortFromCaller();
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException('Polling timed out', 'TimeoutError'));
    }, timeoutMs);
    try {
      while (true) {
        let job: ClassificationJob;
        try { job = await this.job(jobId, controller.signal, options.contextId); }
        catch (error) {
          if (isRetryablePollingFailure(error)) {
            const retryAfterMs = error instanceof ApiError ? (error.retryAfterSeconds ?? 1) * 1000 : intervalMs;
            await abortableDelay(Math.max(intervalMs, retryAfterMs), controller.signal);
            continue;
          }
          throw error;
        }
        if (job.status === 'completed' || job.status === 'failed') return job;
        await abortableDelay(intervalMs, controller.signal);
      }
    } catch (error) {
      if (timedOut) throw new ApiError(408, { error: { code: 'poll_timeout', message: 'Processing is taking longer than expected.', retryable: true } });
      throw error;
    } finally {
      window.clearTimeout(timer);
      options.signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  private async request<T>(path: string, options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
    authenticated?: boolean;
    contextRail?: IdentityRail;
    contextId?: string;
    headers?: Record<string,string>;
    contextRefreshAttempted?: boolean;
  } = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    Object.assign(headers,options.headers);
    if (options.authenticated !== false && token) Object.assign(headers,sessionHeaders(token));
    const rail = options.contextRail ?? contextRailForPath(path);
    const contextId = options.contextId ?? (rail ? this.contextIdForRequest(rail, path, options.body) : undefined);
    if (contextId) headers['x-amoye-context-id'] = contextId;
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
    const payload = await readJson(response);
    if (!response.ok) {
      if (options.authenticated !== false && response.status === 403 && rail
        && !options.contextRefreshAttempted && isInvalidContextDenial(payload)) {
        const refreshed = await this.refreshIdentityContexts(options.signal);
        const retryContextId = refreshedContextForRequest(refreshed.contexts, rail, path, options.body,
          this.resolvedContextForRail(rail));
        if (retryContextId) return this.request<T>(path, { ...options, contextId: retryContextId,
          contextRefreshAttempted: true });
      }
      const error = new ApiError(response.status, payload as ApiErrorPayload, retryAfterSeconds(response));
      if (options.authenticated !== false && response.status === 401 && !this.authenticationFailureNotified) {
        this.authenticationFailureNotified = true;
        this.onAuthenticationRequired?.(error);
      }
      throw error;
    }
    return payload as T;
  }

  private refreshIdentityContexts(signal?: AbortSignal): Promise<IdentityContextsResponse> {
    if (this.identityContextRefresh) return this.identityContextRefresh;
    const refresh = this.request<IdentityContextsResponse>('/api/v2/identity/contexts', {
      signal, contextRefreshAttempted: true,
    }).then((response) => {
      this.onIdentityContextsRefreshed?.(response.contexts);
      return response;
    }).finally(() => {
      if (this.identityContextRefresh === refresh) this.identityContextRefresh = undefined;
    });
    this.identityContextRefresh = refresh;
    return refresh;
  }

  private resolvedContextForRail(rail: IdentityRail): ResolvedIdentityContext | undefined {
    const selected = this.selectedResolvedContexts[rail];
    if (selected) return selected;
    const selectedId = this.selectedContextIds[rail];
    if (selectedId) {
      const resolved = this.getResolvedContexts().find((context) => context.scope.rail === rail
        && context.contextId === selectedId);
      if (resolved) return resolved;
    }
    const provided = this.getResolvedContext(rail);
    if (selectedId && provided?.contextId !== selectedId) return undefined;
    return provided;
  }

  private contextIdForRequest(rail: IdentityRail, path: string, body: unknown): string | undefined {
    const candidates = this.getResolvedContexts().filter((context) => context.scope.rail === rail);
    const explicitId = this.selectedContextIds[rail];
    const explicit = this.selectedResolvedContexts[rail] ?? candidates.find(({ contextId }) => contextId === explicitId);
    const active = this.getResolvedContext(rail);
    const current = explicit ?? active;
    const requestHints = contextHints(path, body);
    const hints: ContextHints = {
      productTrack: requestHints.productTrack ?? current?.scope.productTrack,
      taxSubjectId: requestHints.taxSubjectId ?? current?.scope.taxSubjectId,
      packageId: requestHints.packageId,
    };
    const hinted = candidates.filter((context) => contextMatchesHints(context, hints));
    if (hasContextHints(hints) && hinted.length === 1) return hinted[0]?.contextId;

    if (explicitId) {
      if (!explicit || contextMatchesHints(explicit, hints)) return explicitId;
    }

    if (active && contextMatchesHints(active, hints)) return active.contextId;
    if (!hasContextHints(hints)) return active?.contextId ?? this.getContextId(rail);

    // Do not attach a known-wrong scope. When the available contexts do not
    // uniquely satisfy the request, the feature must call selectContext().
    if (candidates.length > 0) throw new ApiError(409, { error: { code: 'identity_context_ambiguous',
      message: 'Select the taxpayer, package and period context before continuing.', retryable: false } });
    return this.getContextId(rail);
  }
}

function correctedCanonicalValue(fieldPath: string, correction: {
  ntaCode?: string; vatAmountKobo?: string; evidenceCategory?: string;
}): unknown | undefined {
  if (fieldPath.endsWith('.nta_code') && correction.ntaCode !== undefined) {
    const value = correction.ntaCode.trim().toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9._-]{1,63}$/.test(value)) {
      throw new Error('Enter a valid classification code using letters, numbers, dots, underscores or hyphens.');
    }
    return { type: 'enum', schemaId: 'AMOYE-BOOKKEEPING-CATEGORY-V2', value };
  }
  if (fieldPath.endsWith('.vat_amount_kobo') && correction.vatAmountKobo !== undefined) {
    if (!/^-?(0|[1-9]\d*)$/.test(correction.vatAmountKobo)) throw new Error('Enter a valid VAT amount.');
    return { type: 'money', currency: 'NGN', minorUnits: correction.vatAmountKobo };
  }
  if (fieldPath.endsWith('.evidence_category') && correction.evidenceCategory !== undefined) {
    const value = correction.evidenceCategory.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{1,63}$/.test(value)) throw new Error('Choose a valid evidence category.');
    return { type: 'enum', schemaId: 'AMOYE-EVIDENCE-CATEGORY-V2', value };
  }
  return undefined;
}

const EVIDENCE_CATEGORY_VALUES = new Set([
  'income', 'deductions_and_reliefs', 'tax_already_paid', 'identity_and_jurisdiction',
  'sales_and_output_vat', 'expense_and_input_vat', 'expense', 'payroll', 'mixed',
]);

async function browserSha256(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function fileSha256(file: File): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
function evidenceMediaType(file: File): string {
  if (file.type === 'text/csv' || file.type === 'application/csv' || file.name.toLowerCase().endsWith('.csv')) {
    return 'AMOYE.VAT.TRANSACTION_EVIDENCE.CSV';
  }
  return file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
}
function classificationJobEvidenceFormat(job: ClassificationJobSummary): string {
  if (job.mime_type === 'text/csv' || job.mime_type === 'application/csv'
    || job.filename.toLowerCase().endsWith('.csv')) return 'AMOYE.VAT.TRANSACTION_EVIDENCE.CSV';
  return job.mime_type;
}
function periodRange(periodId: string): { periodStart: string; periodEndExclusive: string } {
  if (/^\d{4}$/.test(periodId)) return { periodStart: `${periodId}-01-01`,
    periodEndExclusive: `${Number(periodId) + 1}-01-01` };
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(periodId);
  if (!match) throw new ApiError(409, { error: { code: 'identity_period_invalid',
    message: 'The selected context has an unsupported tax period.', retryable: false } });
  const year = Number(match[1]); const month = Number(match[2]);
  const nextYear = month === 12 ? year + 1 : year; const nextMonth = month === 12 ? 1 : month + 1;
  return { periodStart: `${match[1]}-${match[2]}-01`,
    periodEndExclusive: `${nextYear}-${String(nextMonth).padStart(2, '0')}-01` };
}

interface ContextHints {
  productTrack?: ProductTrack;
  taxSubjectId?: string;
  packageId?: string;
}

function contextHints(path: string, body: unknown): ContextHints {
  const record = body && typeof body === 'object' ? body as Record<string, unknown> : undefined;
  const scope = record?.scope && typeof record.scope === 'object' ? record.scope as Record<string, unknown> : undefined;
  const queryTaxpayer = new URL(path, 'https://amoye.invalid').searchParams.get('taxpayerId') ?? undefined;
  const productTrack = record?.track === 'A' || record?.track === 'B1' || record?.track === 'B2'
    ? record.track : scope?.productTrack === 'A' || scope?.productTrack === 'B1' || scope?.productTrack === 'B2'
      ? scope.productTrack : undefined;
  return {
    ...(productTrack ? { productTrack } : {}),
    ...(typeof record?.taxpayerId === 'string' ? { taxSubjectId: record.taxpayerId }
      : typeof scope?.taxSubjectId === 'string' ? { taxSubjectId: scope.taxSubjectId }
        : queryTaxpayer ? { taxSubjectId: queryTaxpayer } : {}),
    ...(typeof record?.packageId === 'string' ? { packageId: record.packageId }
      : typeof scope?.packageId === 'string' ? { packageId: scope.packageId } : {}),
  };
}

function hasContextHints(hints: ContextHints): boolean {
  return !!(hints.productTrack || hints.taxSubjectId || hints.packageId);
}

function contextMatchesHints(context: ResolvedIdentityContext, hints: ContextHints): boolean {
  return (!hints.productTrack || context.scope.productTrack === hints.productTrack)
    && (!hints.taxSubjectId || context.scope.taxSubjectId === hints.taxSubjectId)
    && (!hints.packageId || context.scope.packageId === hints.packageId);
}

function refreshedContextForRequest(contexts: readonly ResolvedIdentityContext[], rail: IdentityRail,
  path: string, body: unknown, previous?: ResolvedIdentityContext): string | undefined {
  const candidates = contexts.filter((context) => context.scope.rail === rail);
  if (previous) {
    const sameScope = candidates.find((context) => identityScopeKey(context.scope) === identityScopeKey(previous.scope));
    if (sameScope) return sameScope.contextId;
  }
  const hints = contextHints(path, body);
  const matching = candidates.filter((context) => contextMatchesHints(context, hints));
  return matching.length === 1 ? matching[0]?.contextId : undefined;
}

function identityScopeKey(scope: IdentityScope): string {
  return [scope.workspaceId, scope.taxSubjectId, scope.engagementId, scope.packageId, scope.periodId,
    scope.productTrack, scope.rail].join('\u001f');
}

function isInvalidContextDenial(payload: unknown): boolean {
  return !!payload && typeof payload === 'object'
    && (payload as { outcome?: unknown }).outcome === 'DENIED'
    && (payload as { reason?: unknown }).reason === 'context_invalid';
}

function deniedRequestMessage(reason: ApiErrorPayload['reason']): string | undefined {
  if (reason === 'context_invalid') return 'Your selected taxpayer context expired. Refresh it and try again.';
  if (reason === 'authority_stale') return 'Your authority changed. Refresh this page before continuing.';
  if (reason === 'action_denied') return 'You are not authorised to complete this action.';
  return undefined;
}

function contextRailForPath(path: string): IdentityRail | undefined {
  if (path.startsWith('/api/v2/e-invoices')) return 'e_invoice';
  if (path.startsWith('/api/v2/tax-returns') || path.startsWith('/api/v2/tax-position')) return 'tax_return';
  return undefined;
}

export function resolveApiBaseUrl(candidate?: string, policy: ApiRoutingPolicy = {
  pageOrigin: globalThis.location?.origin ?? 'http://localhost',
  production: import.meta.env.PROD,
}): string {
  return validateApiOrigin(candidate, policy);
}

export async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const stride = 0x8000;
  for (let index = 0; index < bytes.length; index += stride) {
    binary += String.fromCharCode(...bytes.subarray(index, index + stride));
  }
  return btoa(binary);
}

function defaultTokenReader(): string | undefined {
  return undefined;
}

function operatorHeaders(access:OperatorAccess):Record<string,string>{return{'x-amoye-operator-key':access.key,'x-amoye-operator-name':access.name};}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: { code: 'invalid_response', message: 'The service returned an unreadable response.' } };
  }
}

function retryAfterSeconds(response: Response): number | undefined {
  const value = response.headers.get('retry-after');
  if (!value || !/^\d+$/.test(value)) return undefined;
  const seconds = Number(value);
  return Number.isSafeInteger(seconds) && seconds >= 1 && seconds <= 3600 ? seconds : undefined;
}

function isRetryablePollingFailure(error: unknown): boolean {
  if (error instanceof ApiError) return error.retryable || error.status === 429 || error.status >= 500;
  // Browser fetch reports a temporarily unreachable/restarting API as a
  // TypeError. The durable job remains in PostgreSQL, so polling can safely
  // resume until the caller's explicit deadline rather than declaring the
  // evidence failed.
  return error instanceof TypeError;
}

function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal?.reason);
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
